/** The decisions `DatabaseWrapper.purgeDatabaseAsync` makes while deleting the user's data from the
 * server, kept apart from it so they can be unit-tested.
 *
 * WHY THIS EXISTS. The purge lists every document on the remote with `allDocs` and then writes
 * `_deleted: true` for each one in `bulkDocs` chunks. `bulkDocs` does NOT throw when an individual
 * document fails — it reports the failure as a row inside its result. The purge used to drop that
 * result into a variable it only logged, so a document whose revision moved between the listing and
 * the write came back as a `conflict` row, was never deleted, and survived on the server — while the
 * purge logged `purged`, the caller signed the user out, and the person was told their private data
 * was gone.
 *
 * The check this file supports asks a different question from the one the neighbouring restore asks.
 * The restore judges each `bulkDocs` row (see `assertBulkDocsChunkSucceeded` in `restore-retry.ts`).
 * The purge instead LISTS THE SERVER AGAIN and asks "is anything still there?", which is the question
 * the user actually cares about. That has three advantages worth keeping:
 *
 *  - A conflict row means the document's revision moved, and the right answer to that is to re-read
 *    the new revision and delete again — which converges — rather than to fail.
 *  - It absorbs the retry case for free. A chunk the server already applied leaves the document gone,
 *    so the next listing simply does not mention it.
 *  - It needs no ruling on per-row conflict semantics, including what CouchDB answers when one
 *    `bulkDocs` call carries both the winning revision and a losing revision of the same document,
 *    which is a shape the purge really does build.
 *
 * The rules a future change could contradict without noticing — that a purge may fail with the user
 * still signed in, that `_design/*` documents are deleted but may not decide the verdict, and why the
 * throw must sit before `remoteDb.close()` — are recorded in `design-docs/boot-sequence.md`, beside the
 * restore's own never-fail-silently rules.
 */

/** How many times the purge may list the remote database.
 *
 * The last listing gets no delete round after it — it is there to prove the result — so this allows
 * one deletion pass plus retries. Bounded on purpose: a server that keeps handing documents back
 * must fail the deletion visibly, not spin. */
export const PURGE_MAX_LIST_PASSES = 4;

/** the subset of an `allDocs({ include_docs: true, conflicts: true })` row the purge reads */
export interface PurgeAllDocsRow {
  id: string;
  value?: { rev?: string };
  doc?: { _conflicts?: string[] };
}

/** one row of the `bulkDocs` call that does the deleting */
export interface PurgeDeleteRow {
  _id: string;
  _rev: string;
  _deleted: true;
  db__trans?: string;
}

export interface PurgePassPlan {
  /** every row to send to `bulkDocs` this pass: each document's winning revision, then the losing
   * revisions of any document that has conflicting branches */
  deletes: PurgeDeleteRow[];
  /** just the losing-branch rows out of `deletes`, so the caller can report how many it pruned */
  conflictRevDeletes: PurgeDeleteRow[];
  /** the ids in this listing that hold the user's own data. After a deletion pass these must all be
   * gone; whatever is still here at the last listing is what the user was wrongly told was deleted. */
  userDocIds: string[];
}

/** True for a document that is ours rather than the user's, and so is not what "delete my private
 * data" promises to erase.
 *
 * Only `_design/*` qualifies. Those are our view definitions: the client never replicates them (see
 * `DatabaseWrapper.syncExcludedDocIds`), they are put on the remote by our deploy tooling, and they
 * hold nothing about the user. The purge still ASKS the server to delete them, exactly as it always
 * has — this only says they may not be the reason a deletion is reported as having failed. Whether a
 * client is even permitted to delete a design document depends on the database's CouchDB permissions,
 * so letting one decide the verdict risks failing every purge over a document nobody cares about.
 *
 * `_local/*` needs no arm here: `allDocs` never returns local documents at all. */
export function isPurgeVerificationExemptId(id: string): boolean {
  return id.startsWith('_design/');
}

/** Work out what one deletion pass should write, and what it must afterwards make disappear. */
export function planPurgeDeletePass(rows: PurgeAllDocsRow[], transactionId: string): PurgePassPlan {
  const winnerDeletes: PurgeDeleteRow[] = [];
  const conflictRevDeletes: PurgeDeleteRow[] = [];
  const userDocIds: string[] = [];

  // for each document currently on the server
  for (const row of rows) {
    if (!isPurgeVerificationExemptId(row.id)) {
      // recorded BEFORE the revision check below, so a row we cannot build a delete for still counts
      // as a document the user's data lives in. Failing the other way would let an unreadable listing
      // row report a deletion as complete.
      userDocIds.push(row.id);
    }

    const winningRev = row.value?.rev;
    if (winningRev) {
      winnerDeletes.push({ _id: row.id, _rev: winningRev, _deleted: true, db__trans: transactionId });
    }

    // for each losing branch of a conflicted document, close that branch too — otherwise it becomes
    // the winner the moment the winning branch is deleted, and the document stays alive
    for (const conflictRev of row.doc?._conflicts ?? []) {
      // deliberately NOT stamped with db__trans, matching what the purge has always written for these
      // rows. `deleteRevisionAsync` in database-wrapper.ts stamps its own and says why it improves on
      // this precedent rather than copying it.
      conflictRevDeletes.push({ _id: row.id, _rev: conflictRev, _deleted: true });
    }
  }

  return {
    deletes: winnerDeletes.concat(conflictRevDeletes),
    conflictRevDeletes,
    userDocIds,
  };
}

/** Add this pass's losing-branch rows to the running list, skipping any revision already in it.
 *
 * Needed because each pass derives `_conflicts` from its OWN listing. A losing branch that a delete
 * round failed to close is reported again by the next listing, so a plain append would count it once
 * per pass — and this list is what `sync_conflicts_pruned` reports, which
 * `design-docs/telemetry.md` describes as the number of branches the purge tried to close, not the
 * number of attempts it made. */
export function mergeConflictRevDeletes(
  alreadyCollected: PurgeDeleteRow[],
  fromThisPass: PurgeDeleteRow[],
): PurgeDeleteRow[] {
  const seen = new Set(alreadyCollected.map(row => `${row._id}@${row._rev}`));
  const merged = alreadyCollected.slice();

  // for each losing branch this pass found, keep it only if no earlier pass already listed it
  for (const row of fromThisPass) {
    const key = `${row._id}@${row._rev}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }

  return merged;
}

/** Thrown when the purge finished its passes and the user's documents are still on the server.
 *
 * The caller (`purgeAccountAsync` in `user-profile.ts`) must let this reach the user and must NOT sign
 * them out: the deletion they asked for did not happen, and telling them it did is the whole defect
 * this error exists to stop. */
export class PurgeIncompleteError extends Error {
  constructor(public readonly survivingIds: string[], listPasses: number) {
    super(`[purge] ${survivingIds.length} document(s) are still on the server after ${listPasses} listing(s)`
      + `: ${survivingIds.slice(0, 20).join(', ')}`);
    this.name = 'PurgeIncompleteError';
  }
}
